import type { AnchorHTMLAttributes, ButtonHTMLAttributes, ReactNode } from 'react';
import { cx } from '../../utils/cx';
import styles from './Button.module.css';

export type ButtonVariant = 'default' | 'primary' | 'ghost' | 'danger';

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className'> {
  variant?: ButtonVariant;
  small?: boolean;
  loading?: boolean;
  icon?: ReactNode;
  children?: ReactNode;
}

// `loading` навмисно ставить `disabled` — подвійний клік на «Видалити» реальний.
export function Button({
  variant = 'default',
  small = false,
  loading = false,
  icon,
  children,
  disabled,
  type = 'button',
  ...rest
}: ButtonProps) {
  const classes = buttonClasses(variant, small);

  return (
    <button
      type={type}
      className={classes}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading ? <span className={styles.spinner} aria-hidden="true" /> : icon}
      {children}
    </button>
  );
}

export interface ButtonLinkProps extends Omit<
  AnchorHTMLAttributes<HTMLAnchorElement>,
  'className'
> {
  variant?: ButtonVariant;
  small?: boolean;
  icon?: ReactNode;
  children?: ReactNode;
}

/**
 * Той самий вигляд, але справжнє посилання: перехід за адресою — робота
 * браузера, і `<button onClick>` відібрав би нову вкладку та контекстне меню.
 */
export function ButtonLink({
  variant = 'default',
  small = false,
  icon,
  children,
  ...rest
}: ButtonLinkProps) {
  return (
    <a className={buttonClasses(variant, small)} {...rest}>
      {icon}
      {children}
    </a>
  );
}

function buttonClasses(variant: ButtonVariant, small: boolean): string {
  return cx(styles.btn, variant !== 'default' && styles[variant], small && styles.sm);
}
